import { createElement } from "react";
import SvgIcon from "@mui/material/SvgIcon";
import type { Components, Theme } from "@mui/material/styles";
import {
  Checkmark as CheckmarkIcon,
  Indeterminate as IndeterminateIcon,
} from "@app/components/icons/uiControlIcons.tsx";

import {
  borderWidth,
  color,
  easing,
  fontSize,
  fontStack,
  fontWeight,
  lineHeight,
  opacity,
  radius,
  spacing,
  transition,
  weave,
} from "@app/theme/weaveTokens.ts";

type ThemeComponents = Components<Omit<Theme, "components">>;

type Variant = "contained" | "outlined" | "text";
type Size = "defaultSize" | "dense";

const variants: Variant[] = ["contained", "outlined", "text"];

type State = "default" | "hover" | "focus" | "pressed" | "disabled";

const sizeTokens = {
  defaultSize: {
    fontSize: fontSize.large,
    lineHeight: lineHeight.compact,
    minWidth: "80px",
    paddingY: weave.generic.px["9"],
    paddingX: spacing.px20,
  },
  dense: {
    fontSize: fontSize.medium,
    lineHeight: weave.generic["line-height"]["20"],
    minWidth: weave.generic.size["64"],
    paddingY: weave.generic.px["5"],
    paddingX: spacing.s,
  },
};

const variantTokens = {
  contained: {
    backgroundColor: color.fillPrimary,
    borderColor: color.ink,
    color: color.inkOnFill,
  },
  outlined: {
    backgroundColor: color.transparent,
    borderColor: color.ink,
    color: color.ink,
  },
  text: {
    backgroundColor: color.transparent,
    borderColor: color.transparent,
    color: color.ink,
  },
};

/** The halo each state draws around the button. */
const stateShadow = (state: State) => {
  if (state === "hover")
    return `0px 0px 0px ${borderWidth.thin} ${color.irisHover}`;
  if (state === "pressed")
    return `0px 0px 0px ${borderWidth.thick} ${color.success}`;
  return "none";
};

/**
 * DHIG's `button.*` component tokens (one object per variant, size and state),
 * which Weave has no equivalent for: Weave buttons are a different design. Built
 * from Weave tokens with DHIG's values.
 */
const buttonState = (variant: Variant, size: Size, state: State) => {
  const { paddingX, paddingY, ...sizing } = sizeTokens[size];
  return {
    ...variantTokens[variant],
    ...sizing,
    borderStyle: "solid",
    borderWidth: borderWidth.thin,
    boxShadow: stateShadow(state),
    fontWeight: fontWeight.medium,
    opacity: state === "disabled" ? opacity.disabled : opacity.visible,
    paddingBottom: paddingY,
    paddingLeft: paddingX,
    paddingRight: paddingX,
    paddingTop: paddingY,
    textTransform: "none",
    ...(state === "focus"
      ? {
          outline: `${borderWidth.thick} solid ${color.iris}`,
          outlineOffset: borderWidth.thin,
        }
      : {}),
  };
};

const buttonTokens = (variant: Variant, size: Size) => ({
  default: buttonState(variant, size, "default"),
  hover: buttonState(variant, size, "hover"),
  focus: buttonState(variant, size, "focus"),
  pressed: buttonState(variant, size, "pressed"),
  disabled: buttonState(variant, size, "disabled"),
});

/**
 * DHIG's button styles (`getButtonStyles` in its
 * theme-base), evaluated statically from the button tokens.
 */
const buttonStyles = (size: Size, variant: Variant) => {
  const labelSize = size === "dense" ? fontSize.medium : fontSize.large;
  const t = buttonTokens(variant, size);
  return {
    ...t.default,
    alignItems: "flex-start",
    // The tokens carry no height; Weave pins 40px and 32px.
    height: "auto",
    fontSize: labelSize,
    "&:hover": { ...t.hover, fontSize: labelSize },
    "&.Mui-disabled": { ...t.disabled, fontSize: labelSize },
    "&:focus-visible": { ...t.focus, fontSize: labelSize },
    "&:focus:active": { ...t.pressed, fontSize: labelSize },
    "&:active": { ...t.pressed, fontSize: labelSize },
  };
};

const expandedBackground = (variant: Variant) =>
  variant === "contained"
    ? {
        backgroundImage: `linear-gradient(${color.activatedFillOnFill}, ${color.activatedFillOnFill}), linear-gradient(${color.fillPrimary}, ${color.fillPrimary})`,
      }
    : { backgroundColor: color.activatedFillSubtle };

/**
 * DHIG Button, restored over Weave's. The DHIG rules sit under `&&` so they
 * beat Weave's `&.MuiButton-contained` colour rules and its `size="small"`
 * variant (16px text, 32px height, 4px padding, `ArtifaktElement` with no
 * fallback). `link-button` is not used in the app, so Weave's stays.
 */
const buttonOverrides: ThemeComponents["MuiButton"] = {
  styleOverrides: {
    root: {
      '& [class*="MuiTouchRipple-root"]': { display: "none" },
      "&:has([class*='MuiButton-endIcon'])": { textAlign: "right" },
      "&:has([class*='MuiButton-startIcon'])": { textAlign: "left" },
      ...Object.fromEntries(
        variants.map((variant) => [
          `&&.MuiButton-${variant}`,
          {
            ...buttonStyles("defaultSize", variant),
            "&[aria-expanded='true']": expandedBackground(variant),
            "&.MuiButton-sizeSmall": {
              ...buttonStyles("dense", variant),
              fontFamily: fontStack,
            },
          },
        ])
      ),
    },
    // Weave's margins (0 7px 0 0) replace MUI's, which DHIG keeps.
    startIcon: {
      marginRight: spacing.xs,
      marginLeft: `-${spacing.xxs}`,
      "&.MuiButton-iconSizeSmall": { marginLeft: "-2px" },
    },
    endIcon: {
      marginLeft: spacing.xs,
      marginRight: `-${spacing.xxs}`,
      "&.MuiButton-iconSizeSmall": { marginRight: "-2px" },
    },
    iconSizeMedium: { "& > *:first-of-type": { fontSize: "inherit" } },
  },
};

const iconButtonStyles = (size: Size, variant: Variant, fullWidth = false) => {
  const sizeOverrides =
    size === "dense"
      ? { paddingBottom: "5px", paddingTop: "5px" }
      : { paddingBottom: "7px", paddingTop: "7px" };
  const widthOverrides = fullWidth
    ? { width: "100%" }
    : {
        minWidth: "auto",
        paddingLeft: spacing.s,
        paddingRight: spacing.s,
      };
  const t = buttonTokens(variant, size);
  const extra = { ...sizeOverrides, ...widthOverrides };
  return {
    ...t.default,
    ...extra,
    "&:hover": { ...t.hover, ...extra },
    "&.Mui-disabled": { ...t.disabled, ...extra },
    "&:focus": { ...t.focus, ...extra },
    "&:focus:active": { ...t.pressed, ...extra },
    "&:active": { ...t.pressed, ...extra },
  };
};

const iconButtonSize = (size: Size, iconSize: string) => ({
  "& [class*='MuiSvgIcon-root']": { fontSize: iconSize },
  ...Object.fromEntries(
    variants.flatMap((variant) => [
      [
        `&.DhigIconButton--variant--${variant}`,
        iconButtonStyles(size, variant),
      ],
      [
        `&.DhigIconButton--variant--${variant}.DhigIconButton--fullWidth`,
        iconButtonStyles(size, variant, true),
      ],
    ])
  ),
});

/**
 * DHIG IconButton, restored over Weave's (which pins a border, black fill for
 * `contained`, 7px/12px padding and its own hover and focus rings).
 */
const iconButtonOverrides: ThemeComponents["MuiIconButton"] = {
  styleOverrides: {
    root: {
      borderRadius: radius.medium,
      "& [class*='MuiSvgIcon-root']": { color: "inherit" },
      "&&.MuiIconButton-sizeSmall": iconButtonSize("dense", "20px"),
      "&&.MuiIconButton-sizeMedium": iconButtonSize("defaultSize", "24px"),
    },
  },
};

/**
 * DHIG Checkbox, restored over Weave's boxed style (Weave draws the box on the
 * root element, DHIG draws it on the child svg).
 */
const checkboxOverrides: ThemeComponents["MuiCheckbox"] = {
  defaultProps: {
    checkedIcon: createElement(
      SvgIcon,
      null,
      createElement(CheckmarkIcon, null)
    ),
    icon: createElement(SvgIcon, null),
    indeterminateIcon: createElement(
      SvgIcon,
      null,
      createElement(IndeterminateIcon, null)
    ),
  },
  styleOverrides: {
    root: {
      "&&": {
        backgroundColor: "transparent",
        border: 0,
        borderRadius: "50%",
        padding: "0px",
        margin: `${spacing.xs} 0`,
        outlineOffset: 0,
        boxShadow: "none",
        transitionDuration: "unset",
        '& [class*="MuiSvgIcon-fontSizeSmall"]': {
          height: spacing.m,
          width: spacing.m,
        },
        "&.Mui-disabled": {
          opacity: opacity.disabled,
          color: color.ink,
        },
        "&:hover": { backgroundColor: "inherit", boxShadow: "none" },
        "&:hover > svg": {
          outline: `${borderWidth.thin} solid ${color.irisHover}`,
          ...transition.activate,
        },
        "&:focus, &.Mui-focusVisible, &:focus-visible": { boxShadow: "none" },
        "&:focus, &.Mui-focusVisible, &:focus-visible > svg": {
          outline: `${borderWidth.thick} solid ${color.iris}`,
          outlineOffset: borderWidth.thin,
          borderRadius: radius.small,
          transition: `outline-color ${transition.activate.transitionDuration} ${easing.energetic}`,
        },
        "&:active": { boxShadow: "none" },
        "&:active > svg": {
          outline: `${borderWidth.thick} solid ${color.success}`,
          ...transition.activate,
        },
        "&.Mui-checked": {
          backgroundColor: "transparent",
          "& [class*='MuiSvgIcon-root']": {
            height: spacing.px20,
            width: spacing.px20,
          },
        },
        '&.Mui-checked:not([class*="MuiCheckbox-indeterminate"])': {
          "& > svg": {
            color: color.inkOnFill,
            background: color.fillPrimary,
          },
          "& span": { background: color.fillPrimary },
        },
        "& > svg": {
          height: spacing.px20,
          width: spacing.px20,
          padding: "2px",
          border: `${borderWidth.thin} solid ${color.ink}`,
          borderRadius: radius.small,
          boxSizing: "border-box",
          ...transition.blur,
        },
        '& + [class*="MuiFormControlLabel-label"]': {
          color: color.ink,
          paddingLeft: spacing.xs,
          fontSize: fontSize.large,
          lineHeight: lineHeight.compact,
        },
      },
    },
    colorSecondary: { color: color.ink },
  },
};

const spinnerSize = (size: string, stroke: string) => ({
  width: size,
  height: size,
  '& svg [class*="MuiCircularProgress-circle"]': { strokeWidth: stroke },
});

/**
 * DHIG CircularProgress (Weave already has the same spin animations). Its named sizes, track and fill colours exist only in
 * DHIG's theme, so under Weave the spinner collapsed to 0x0. Everything is
 * scoped to DHIG's own `DhigCircularProgress--*` classes, so a plain MUI
 * CircularProgress keeps Weave's look.
 */
const circularProgressOverrides: ThemeComponents["MuiCircularProgress"] = {
  styleOverrides: {
    root: {
      "&.DhigCircularProgress--background": {
        color: color.trackFaint,
      },
      "&.DhigCircularProgress--foreground": {
        color: color.ink,
        position: "absolute",
        left: 0,
      },
      "&.DhigCircularProgress--size--x-small": spinnerSize("14px", "4.4"),
      "&.DhigCircularProgress--size--small": spinnerSize(spacing.l, "3.5"),
      "&.DhigCircularProgress--size--medium": spinnerSize(spacing.xxl, "2.6"),
      "&.DhigCircularProgress--size--large": spinnerSize("112px", "1.2"),
    },
  },
};

/**
 * DHIG's CircularProgress is rendered inside two MUI Containers. DHIG's own
 * theme sizes them to the spinner (`fit-content`, no margin or padding). Weave
 * styles every MuiContainer as a full-width page container (width 100%, large
 * max-width, grid margins on child divs), so in a wide parent the foreground
 * arc, positioned absolutely at left 0 of the container, sat at the parent's
 * left edge while the track stayed centred. This restates DHIG's layout,
 * scoped to the DHIG classes so a plain MUI Container keeps Weave's look.
 */
const circularProgressContainerOverrides: ThemeComponents["MuiContainer"] = {
  styleOverrides: {
    root: {
      "&&.DhigCircularProgress--wrapper": {
        width: "fit-content",
        margin: 0,
        padding: 0,
        "& > .DhigCircularProgress--container": {
          justifyContent: "center",
          position: "relative",
          width: "unset",
          margin: 0,
          padding: 0,
        },
      },
    },
  },
};

/**
 * Weave gives every FormControlLabel label a 12px right margin and sets the
 * label of a medium control to a 24px line height. DHIG has neither, so a
 * Checkbox in a table cell (the select column of the app index) came out 12px
 * wider than on main, which pushed every following column right. Putting the
 * margin back to 0 matches DHIG for all controls, and the line height is
 * restated for checkboxes only, like the `& + label` rule in the Checkbox
 * slot above.
 */
const formControlLabelOverrides: ThemeComponents["MuiFormControlLabel"] = {
  styleOverrides: {
    root: {
      '&& [class*="MuiCheckbox-root"] + [class*="MuiFormControlLabel-label"]': {
        lineHeight: lineHeight.compact,
      },
    },
    label: { marginRight: 0 },
  },
};

export const controlOverrides: ThemeComponents = {
  MuiContainer: circularProgressContainerOverrides,
  MuiFormControlLabel: formControlLabelOverrides,
  MuiButton: buttonOverrides,
  MuiIconButton: iconButtonOverrides,
  MuiCheckbox: checkboxOverrides,
  MuiCircularProgress: circularProgressOverrides,
};
